/**
 * Recent Question Model
 *
 * Handles API operations for individual recent questions
 * Corresponds to the recent_questions table in the backend
 * Follows the warbler pattern similar to SupplierModel
 */

import { get, post, put, del } from '@/services/http';
import {
  IRecentQuestion,
  ICreateRecentQuestionRequest,
  IQuestionAnalytics,
  IQuestionMetadata,
  QuestionCategory,
  QuestionStatus,
  QuestionSource,
} from '@/models/insights/';

export class RecentQuestionModel {

  /**
   * Get recent question by ID
   */
  public static async getById(id: number): Promise<RecentQuestionModel | null> {
    try {
      const response: any = await get(`/recent-questions/${id}`);
      if (response.data && response.data.success && response.data.data) {
        return new RecentQuestionModel(response.data.data);
      }
      return null;
    } catch (error) {
      console.error('Error fetching recent question:', error);
      return null;
    }
  }

  /**
   * Get all recent questions with optional filtering
   */
  public static async getAll(params?: any): Promise<RecentQuestionModel[]> {
    try {
      const response: any = await get('/recent-questions', { params });
      if (response.data && response.data.success && response.data.data) {
        return response.data.data.map((item: IRecentQuestion) => new RecentQuestionModel(item));
      }
      return [];
    } catch (error) {
      console.error('Error fetching recent questions:', error);
      return [];
    }
  }
  public id: number;
  public question_text: string;
  public instruction: string;
  public question_hash: string;
  public measure_code: string;
  public dimension_code?: string;
  public user_id?: number;
  public merchant_id: number;
  public status: QuestionStatus;
  public source: QuestionSource;
  public execution_count: number;
  public created_at: string;
  public updated_at: string;
  public last_executed_at?: string;
  public metadata?: IQuestionMetadata;
  public analytics?: IQuestionAnalytics;

  constructor(data: Partial<IRecentQuestion> = {}) {
    const parameters = (data as any).parameters;

    this.id = data.id || 0;
    this.question_text = data.question_text || '';
    this.instruction = data.instruction || '';
    this.question_hash = data.question_hash || '';

    // Extract measure_code and dimension_code from parameters object if available
    // API response structure: { parameters: { measure_code: "code", dimension_code: "code" } }
    this.measure_code = data.measure_code ||
                       (parameters?.measure_code) ||
                       '';
    this.dimension_code = data.dimension_code ||
                         (parameters?.dimension_code) ||
                         undefined;
    this.user_id = data.user_id;
    this.merchant_id = data.merchant_id || 0;
    this.status = data.status || QuestionStatus.ACTIVE;
    this.source = data.source || QuestionSource.CUSTOM;
    this.execution_count = data.execution_count || 0;
    this.created_at = data.created_at || '';
    this.updated_at = data.updated_at || '';
    this.last_executed_at = data.last_executed_at;
    this.metadata = data.metadata;
    this.analytics = data.analytics;
  }

  /**
   * Save recent question (create or update)
   */
  public async save(): Promise<boolean> {
    try {
      if (this.id) {
        await this.update();
      } else {
        await this.create();
      }
      return true;
    } catch (error) {
      console.error('Error saving recent question:', error);
      return false;
    }
  }

  /**
   * Delete recent question (soft delete)
   */
  public async delete(): Promise<boolean> {
    try {
      const response: any = await del(`/recent-questions/${this.id}`);
      // Fix: Check response.success instead of response.data.success (warbler HTTP service pattern)
      if (response && response.success) {
        this.status = QuestionStatus.DELETED;
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error deleting recent question:', error);
      return false;
    }
  }

  /**
   * Execute the question to get fresh results
   */
  public async execute(): Promise<any> {
    try {
      const response: any = await post(`/recent-questions/${this.id}/execute`);

      if (response.data && response.data.success && response.data.data) {
        // Update execution count and last executed time
        this.execution_count++;
        this.last_executed_at = new Date().toISOString();

        // Update analytics if returned
        if (response.data.data.analytics) {
          this.analytics = response.data.data.analytics;
        }

        return response.data.data;
      }

      return null;
    } catch (error) {
      console.error('Error executing question:', error);
      throw error;
    }
  }

  /**
   * Pin this question
   */
  public async pin(pinReason: string = 'frequently_used'): Promise<boolean> {
    try {
      const response: any = await post(`/recent-questions/${this.id}/pin`, {
        pin_reason: pinReason,
      });

      if (response.data && response.data.success) {
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error pinning question:', error);
      return false;
    }
  }

  /**
   * Unpin this question
   */
  public async unpin(): Promise<boolean> {
    try {
      const response: any = await post(`/recent-questions/${this.id}/unpin`);

      if (response.data && response.data.success) {
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error unpinning question:', error);
      return false;
    }
  }

  /**
   * Get category icon for UI display
   */
  public getCategoryIcon(): string {
    const category = this.getCategoryFromMetadata();
    const iconMap: Record<string, string> = {
      [QuestionCategory.SALES_OVERVIEW]: 'mdi-chart-line',
      [QuestionCategory.INVENTORY_FORECASTING]: 'mdi-package-variant',
      [QuestionCategory.CUSTOMER_INSIGHTS]: 'mdi-account-group',
      [QuestionCategory.PRODUCT_ANALYTICS]: 'mdi-trending-up',
      [QuestionCategory.FINANCIAL_REPORTING]: 'mdi-currency-usd',
      [QuestionCategory.OPERATIONS_MONITORING]: 'mdi-cog',
      [QuestionCategory.GENERAL_QUERY]: 'mdi-help-circle',
      [QuestionCategory.ORDER_ANALYTICS]: 'mdi-cart',
      [QuestionCategory.MARKETING_ANALYTICS]: 'mdi-bullhorn',
      [QuestionCategory.SUPPLIER_ANALYTICS]: 'mdi-truck',
      [QuestionCategory.CUSTOM]: 'mdi-star',
    };
    return this.metadata?.icon || iconMap[category] || 'mdi-help-circle';
  }

  /**
   * Get category color for UI display
   */
  public getCategoryColor(): string {
    const category = this.getCategoryFromMetadata();
    const colorMap: Record<string, string> = {
      [QuestionCategory.SALES_OVERVIEW]: 'success',
      [QuestionCategory.INVENTORY_FORECASTING]: 'warning',
      [QuestionCategory.CUSTOMER_INSIGHTS]: 'info',
      [QuestionCategory.PRODUCT_ANALYTICS]: 'primary',
      [QuestionCategory.FINANCIAL_REPORTING]: 'secondary',
      [QuestionCategory.OPERATIONS_MONITORING]: 'accent',
      [QuestionCategory.GENERAL_QUERY]: 'grey',
      [QuestionCategory.ORDER_ANALYTICS]: 'indigo',
      [QuestionCategory.MARKETING_ANALYTICS]: 'purple',
      [QuestionCategory.SUPPLIER_ANALYTICS]: 'teal',
      [QuestionCategory.CUSTOM]: 'deep-purple',
    };
    return this.metadata?.color || colorMap[category] || 'grey';
  }

  /**
   * Get formatted last executed date
   */
  public getFormattedLastExecuted(): string {
    if (!this.last_executed_at) {
      return 'Never';
    }

    const date = new Date(this.last_executed_at);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) { return 'Just now'; }
    if (diffMins < 60) { return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`; }

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) { return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`; }

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) { return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`; }

    return date.toLocaleDateString();
  }

  /**
   * Check if question needs refresh based on metadata
   */
  public needsRefresh(): boolean {
    if (!this.metadata?.data_freshness) {
      return false;
    }

    if (!this.last_executed_at) {
      return true;
    }

    const lastExecuted = new Date(this.last_executed_at);
    const now = new Date();
    const diffMs = now.getTime() - lastExecuted.getTime();

    const freshnessThresholds: Record<string, number> = {
      real_time: 5 * 60 * 1000,        // 5 minutes
      hourly: 60 * 60 * 1000,          // 1 hour
      daily: 24 * 60 * 60 * 1000,      // 1 day
      weekly: 7 * 24 * 60 * 60 * 1000, // 1 week
      monthly: 30 * 24 * 60 * 60 * 1000, // 30 days
    };

    const threshold = freshnessThresholds[this.metadata.data_freshness] || freshnessThresholds.daily;
    return diffMs > threshold;
  }

  /**
   * Check if this is a pinned question based on recent_questions API response
   */
  public isPinned(): boolean {
    // The backend API includes is_pinned field in response
    return (this as any).is_pinned || false;
  }

  /**
   * Convert to JSON representation
   */
  public toJSON(): IRecentQuestion {
    return {
      id: this.id,
      question_text: this.question_text,
      instruction: this.instruction,
      question_hash: this.question_hash,
      measure_code: this.measure_code,
      dimension_code: this.dimension_code,
      user_id: this.user_id,
      merchant_id: this.merchant_id,
      status: this.status,
      source: this.source,
      execution_count: this.execution_count,
      created_at: this.created_at,
      updated_at: this.updated_at,
      last_executed_at: this.last_executed_at,
      metadata: this.metadata,
      analytics: this.analytics,
    };
  }

  /**
   * Create new recent question
   */
  private async create(): Promise<void> {
    const payload: ICreateRecentQuestionRequest = {
      question_text: this.question_text,
      instruction: this.instruction,
      measure_code: this.measure_code,
      dimension_code: this.dimension_code,
      source: this.source,
      metadata: this.metadata,
      category: this.getCategoryFromMetadata(),
    };

    const response: any = await post('/recent-questions', payload);

    if (response.data && response.data.success && response.data.data) {
      this.updateFromResponse(response.data.data);
    }
  }

  /**
   * Update existing recent question
   */
  private async update(): Promise<void> {
    const payload = {
      question_text: this.question_text,
      instruction: this.instruction,
      measure_code: this.measure_code,
      dimension_code: this.dimension_code,
      metadata: this.metadata,
    };

    const response: any = await put(`/recent-questions/${this.id}`, payload);

    if (response.data && response.data.success && response.data.data) {
      this.updateFromResponse(response.data.data);
    }
  }

  /**
   * Get category from metadata or measure code
   */
  private getCategoryFromMetadata(): string {
    // First check if category is explicitly set in metadata
    if (this.metadata?.tags?.length) {
      const categoryTag = this.metadata.tags.find((tag) =>
        Object.values(QuestionCategory).includes(tag as QuestionCategory),
      );
      if (categoryTag) { return categoryTag; }
    }

    // Map measure code to category
    const measureCategoryMap: Record<string, string> = {
      sales: QuestionCategory.SALES_OVERVIEW,
      inventory: QuestionCategory.INVENTORY_FORECASTING,
      customer: QuestionCategory.CUSTOMER_INSIGHTS,
      product: QuestionCategory.PRODUCT_ANALYTICS,
      financial: QuestionCategory.FINANCIAL_REPORTING,
      operations: QuestionCategory.OPERATIONS_MONITORING,
      order: QuestionCategory.ORDER_ANALYTICS,
      marketing: QuestionCategory.MARKETING_ANALYTICS,
      supplier: QuestionCategory.SUPPLIER_ANALYTICS,
    };

    for (const [key, category] of Object.entries(measureCategoryMap)) {
      if (this.measure_code.toLowerCase().includes(key)) {
        return category;
      }
    }

    return QuestionCategory.GENERAL_QUERY;
  }

  /**
   * Update model from API response
   */
  private updateFromResponse(data: IRecentQuestion): void {
    this.id = data.id;
    this.question_text = data.question_text;
    this.instruction = data.instruction;
    this.question_hash = data.question_hash;
    this.measure_code = data.measure_code;
    this.dimension_code = data.dimension_code;
    this.user_id = data.user_id;
    this.merchant_id = data.merchant_id;
    this.status = data.status;
    this.source = data.source;
    this.execution_count = data.execution_count;
    this.created_at = data.created_at;
    this.updated_at = data.updated_at;
    this.last_executed_at = data.last_executed_at;
    this.metadata = data.metadata;
    this.analytics = data.analytics;
  }
}
